/**
 * Junto Frontend WaitlistConfirmationPopup Component
 * 
 * Popup for joining a waitlist when a class is full.
 * Theme-agnostic and reusable across different studio brands.
 */

import styled from 'styled-components';
import { Button } from '../ui/index.js';
import { solRoomTheme } from '../../themes/sol-room.js';
import BookingPopupBase, {
  ClassInfoSection,
  NoPaymentOptionsSection,
  NoPaymentButtonContainer,
  ButtonContainer,
  usePaymentValidation
} from './BookingPopupBase';

const WaitlistMessage = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
`;

const WaitlistTitle = styled.h5`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm} 0;
`;

const WaitlistText = styled.p`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
  margin: 0;
  line-height: 1.5;
`;

const WaitlistConfirmationPopup = ({ 
  isOpen, 
  onClose, 
  classData, 
  onConfirm,
  theme = solRoomTheme 
}) => {
  const { hasPaymentOptions } = usePaymentValidation(classData);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(classData);
    }
    onClose();
  };

  if (!classData) return null;

  const userHasPaymentOptions = hasPaymentOptions();

  return (
    <BookingPopupBase 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Join Waitlist" 
      maxWidth="450px"
      theme={theme}
    >
      <ClassInfoSection classData={classData} theme={theme} />

      {userHasPaymentOptions ? (
        <>
          <WaitlistMessage theme={theme}>
            <WaitlistTitle theme={theme}>This class is currently full</WaitlistTitle>
            <WaitlistText theme={theme}>
              Would you like to join the waitlist? You'll be notified if a spot becomes available 
              and will have the opportunity to reserve it.
            </WaitlistText>
          </WaitlistMessage>

          <ButtonContainer theme={theme}>
            <Button
              onClick={onClose}
              variant="primary"
              size="small"
              theme={theme}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              variant="filled"
              size="small"
              theme={theme}
            >
              Join Waitlist
            </Button>
          </ButtonContainer>
        </>
      ) : (
        <>
          <NoPaymentOptionsSection onClose={onClose} theme={theme} />
          <NoPaymentButtonContainer onClose={onClose} buttonText="Close" theme={theme} />
        </>
      )}
    </BookingPopupBase>
  );
};

export default WaitlistConfirmationPopup;